import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useFormContext } from 'react-hook-form-mui';
import DataContainer from '../../../../Utilities/DataContainer';
import RenderSelectField from '../../../../Utilities/Forms/RenderSelectField';
import RenderTextField from '../../../../Utilities/Forms/RenderTextField';
import ValidatedSelectElement from '../../../../Utilities/Forms/ValidatedSelectElement';
import ValidatedTextField from '../../../../Utilities/Forms/ValidatedTextField';
import monthDropdown from '../../../../Utilities/Constants/MonthDropdown';
import yesNoDropdown from '../../../../Utilities/Constants/YesNoDropdown';
interface IProps {
    isReadOnly: boolean
    isResidential: boolean
}
export default function TravelPlanCarParkingV2({ isReadOnly, isResidential }: IProps) {
    const { getValues } = useFormContext();
    const formValues = getValues();
    const hasValues = (fields: string[]) => fields.some(field => formValues[field]) || !isReadOnly;

    return (
        <>
            <Grid container alignItems="flex-start">
                <Grid size={4} container>
                    {hasValues(["parkingRestricted", "parkingRestrictedMonth", "parkingRestrictedYear", "parkingBeforeSite"]) && (
                        <Grid container size={12}>
                            <DataContainer title="Parking Restriction Details">
                                <Stack spacing={1}>
                                    <RenderSelectField name="parkingRestricted" label="Is there restricted availability of on-site parking space (e.g. on-site parking is set at a lower level than forecast demand/parking standards at the time of planning consent)?" readOnly={isReadOnly} options={yesNoDropdown} />
                                    <Grid size={12}>
                                        <Typography variant={"body2"}>Date Implemented</Typography>
                                    </Grid>
                                    <Stack direction="row" spacing={1}>
                                        <Grid size={6}>
                                            <ValidatedSelectElement name={'parkingRestrictedMonth'} options={monthDropdown} readOnly={isReadOnly} />
                                        </Grid>
                                        <Grid size={6}>
                                            <ValidatedTextField name={'parkingRestrictedYear'} readOnly={isReadOnly} />
                                        </Grid>
                                    </Stack>
                                    <RenderSelectField name="parkingBeforeSite" label="In place prior to the development of the site or otherwise independent of it (i.e. not a Travel Plan element)" readOnly={isReadOnly} options={yesNoDropdown} />
                                </Stack>
                            </DataContainer>
                        </Grid>
                    )}

                    {hasValues(["parkingMeasures", "parkingMeasuresMonth", "parkingMeasuresYear"]) && (
                        <Grid container size={12} >
                            <DataContainer title={"Parking Measure Details"}>
                                <Stack spacing={1}>
                                    <RenderSelectField
                                        name="parkingMeasures"
                                        label={isResidential
                                            ? "Are there any parking restrictions/measures/conditions at the site? (if so, provide details, for example permit eligibility restrictions, permits only available to residents not entitled to off-site CPZ parking, etc.)"
                                            : "Are there any parking restrictions/measures/conditions at the site? (for example permit eligibility restrictions, etc.)"}
                                        readOnly={isReadOnly}
                                        options={yesNoDropdown}
                                    />
                                    <Grid size={12}>
                                        <Typography variant={"body2"}>Date Implemented</Typography>
                                    </Grid>
                                    <Stack direction="row" spacing={1}>
                                        <Grid size={6}>
                                            <ValidatedSelectElement name={'carClubMonth'} options={monthDropdown} readOnly={isReadOnly} />
                                        </Grid>
                                        <Grid size={6}>
                                            <ValidatedTextField name={'carClubYear'} readOnly={isReadOnly} />
                                        </Grid>
                                    </Stack>
                                </Stack>
                            </DataContainer>
                        </Grid>
                    )}
                </Grid>

                <Grid size={4} container>
                    {hasValues(["parkingCharges", "parkingChargesMonth", "parkingChargesYear"]) && (
                        <Grid container size={12} >
                            <DataContainer title={"Parking Restriction Charges"}>
                                <Stack spacing={1}>
                                    <RenderSelectField
                                        name="parkingCharges"
                                        label={`Are there on-site parking charges for Travel Plan target groups (e.g. ${isResidential ? "residents" : "staff"}, visitors, etc.)`}
                                        readOnly={isReadOnly}
                                        options={yesNoDropdown}
                                    />
                                    <Grid size={12}>
                                        <Typography variant={"body2"}>Date Implemented</Typography>
                                    </Grid>
                                    <Stack direction="row" spacing={1}>
                                        <Grid size={6}>
                                            <ValidatedSelectElement name={'parkingChargesMonth'} options={monthDropdown} readOnly={isReadOnly} />
                                        </Grid>
                                        <Grid size={6}>
                                            <ValidatedTextField name={'parkingChargesYear'} readOnly={isReadOnly} />
                                        </Grid>
                                    </Stack>
                                </Stack>
                            </DataContainer>
                        </Grid>
                    )}
                    {hasValues(["parkingEnforced", "parkingEnforcedMonth", "parkingEnforcedYear"]) && (
                        <Grid container size={12} >
                            <DataContainer title={"Parking Enforcement Details"}>
                                <Stack spacing={1}>
                                    <RenderSelectField name="parkingEnforced" label="Is there any enforcement of parking measures/restrictions at the site? (for example barrier control, presence of parking attendants/security patrols, clamping, ANPR, etc.)" readOnly={isReadOnly} options={yesNoDropdown} />
                                    <Grid size={12}>
                                        <Typography variant={"body2"}>Date Implemented</Typography>
                                    </Grid>
                                    <Stack direction="row" spacing={1}>
                                        <Grid size={6}>
                                            <ValidatedSelectElement name={'parkingEnforcedMonth'} options={monthDropdown} readOnly={isReadOnly} />
                                        </Grid>
                                        <Grid size={6}>
                                            <ValidatedTextField name={'parkingEnforcedYear'} readOnly={isReadOnly} />
                                        </Grid>
                                    </Stack>
                                </Stack>
                            </DataContainer>
                        </Grid>
                    )}
                </Grid>

                <Grid size={4} container>
                    {hasValues(["evBays", "evBaysMonth", "evBaysYear", "evBaysCount", "evBaysBeforeSite"]) && (
                        <Grid container size={12} >
                            <DataContainer title={"EV Bay Details"}>
                                <Stack spacing={1}>
                                    <RenderSelectField name="evBays" label="Are there site-specific electric vehicle charging bays provided at the site?" readOnly={isReadOnly} options={yesNoDropdown} />
                                    <Grid size={12}>
                                        <Typography variant={"body2"}>Date Implemented</Typography>
                                    </Grid>
                                    <Stack direction="row" spacing={1}>
                                        <Grid size={6}>
                                            <ValidatedSelectElement name={'evBaysMonth'} options={monthDropdown} readOnly={isReadOnly} />
                                        </Grid>
                                        <Grid size={6}>
                                            <ValidatedTextField name={'evBaysYear'} readOnly={isReadOnly} />
                                        </Grid>
                                    </Stack>
                                    <RenderTextField name="evBaysCount" label="Number of bays" readOnly={isReadOnly} />
                                    <RenderSelectField name="evBaysBeforeSite" label="In place prior to the development of the site or otherwise otherwise independent of it (i.e. not a Travel Plan element)" readOnly={isReadOnly} options={yesNoDropdown} />
                                </Stack>
                            </DataContainer>
                        </Grid>
                    )}
                    <Grid container size={12} >
                        <Grid size={12}>
                            <Typography variant={"h6"}>Additional Comments</Typography>
                        </Grid>
                        <RenderTextField name="parkingComments" label="Specify details of all Travel Plan elements identified in this section, along with any other car parking features not already covered." readOnly={isReadOnly} multiLine={true} rows={4} labelSize={12} inputSize={12} alwaysShow={true} />
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
}
